import React from "react";
import {
  Grid as MuiGrid,
  Paper,
  Typography,
  Box,
  FormHelperText,
} from "@mui/material";

import RpImageTextInline from "@/components/RpImageTextInline";

import {
  getFormatDate,
  formattedAmount,
  RPNumberFormat,
} from "@/helpers/utils";
import ViewRRDLocationTemplate from "./ViewRRDLocationTemplate";
import { PRINT_TYPE } from "@/features/campaigns/direct-mail/rrd/utils/rrdConstants";
import {
  RRD_LAYOUT_KEY,
  normalizeRRDPrintTypeContext,
} from "@/features/campaigns/direct-mail/rrd/utils/rrdLayoutHelper";

type RRDJoinConfirmLocationCardProps = {
  location?: Record<string, any> | null;
};

const Grid = MuiGrid as React.ElementType;

const RRDJoinConfirmLocationCard = ({
  location = {},
}: RRDJoinConfirmLocationCardProps) => {
  if (!location) return null;

  const printTypeContext = normalizeRRDPrintTypeContext({
    print_type: location.printType,
    printTypeLabel: location.printTypeLabel,
    layout_key: location.layout_key,
  });
  // location print type
  const isPrintTypePostCard =
    printTypeContext.printTypeLabel === PRINT_TYPE.POSTCARD.label;
  const isPrintTypeValue =
    printTypeContext.layoutKey === RRD_LAYOUT_KEY.VALUE_SHEET_INSERT_2H_12C;

  // Transform API response to match PostcardLocationCard structure
  const locationName = location?.locationName || "N/A";
  const mailType = isPrintTypeValue
    ? PRINT_TYPE.VALUE.label
    : isPrintTypePostCard
      ? PRINT_TYPE.POSTCARD.label
      : printTypeContext.printTypeLabel || "";

  const mailingDate = location.mailingDate
    ? getFormatDate(location.mailingDate, "MMM DD, YYYY")
    : "N/A";
  const couponExpirationFront = location.frontCouponExpiryDate
    ? getFormatDate(location.frontCouponExpiryDate, "MMM DD, YYYY")
    : "N/A";
  const couponExpirationBack = location.backCouponExpiryDate
    ? getFormatDate(location.backCouponExpiryDate, "MMM DD, YYYY")
    : null;

  // Transform distribution data
  const hasDistribution = location?.distribution;
  const distribution = hasDistribution
    ? {
        distribution_enable: location?.distribution,
        quantity: location?.estimatedDistribution
          ? RPNumberFormat().to(location.estimatedDistribution)
          : 0,
        cost: formattedAmount(location.distributionCost || 0),
      }
    : null;

  // Transform store copy data
  const hasStoreCopy = location.storeCopy;
  const addressSource = location?.locationInfo;
  const locationAddress =
    hasStoreCopy && addressSource
      ? [
          addressSource.street_address,
          addressSource.city,
          addressSource.state,
          addressSource.zip_code,
        ]
          .filter(Boolean)
          .join(", ")
      : "";
  const storeCopy = hasStoreCopy
    ? {
        storeCopy_enable: location.storeCopy,
        quantity: location.storeCopyQnt
          ? RPNumberFormat().to(location.storeCopyQnt)
          : 0,
        cost: formattedAmount(location.storeCopyCost || 0),
      }
    : null;
  const hasCouponExpiration = couponExpirationFront || couponExpirationBack;

  const distributionValue = hasDistribution ? "Y" : "N";
  const storeCopyValue = hasStoreCopy ? "Y" : "N";

  // Get template image from first hero image
  const templateImage =
    location.heroImages && location.heroImages.length > 0
      ? location.heroImages[0].compress_url ||
        location.heroImages[0].thumbnail_url
      : null;

  return (
    <Paper className="mb-3 p-3 mt-3" elevation={2}>
      <Grid container spacing={3}>
        {locationName && (
          <Grid item size={{ xs: 12, sm: 12, md: "grow" }}>
            <RpImageTextInline
              textProps={{ style: { display: "grid" } }}
              alignCenter={false}
              svgIcon="location_mark"
              imageProps={{ color: "primary" }}
              text={
                <>
                  <Typography variant="caption">Location</Typography>
                  <Typography
                    className="title-val fw-b mt-2"
                    component="p"
                    variant="caption"
                  >
                    {locationName}
                  </Typography>
                </>
              }
            />
          </Grid>
        )}

        {mailType && (
          <Grid item size={{ xs: 6, sm: 6, md: "grow" }}>
            <RpImageTextInline
              textProps={{ style: { display: "grid" } }}
              alignCenter={false}
              svgIcon="mail_type"
              imageProps={{ color: "primary" }}
              text={
                <>
                  <Typography variant="caption">Mail Type</Typography>
                  <Typography
                    className="title-val fw-b mt-2"
                    component="p"
                    variant="caption"
                  >
                    {mailType}
                  </Typography>
                </>
              }
            />
          </Grid>
        )}

        {mailingDate && (
          <Grid item size={{ xs: 6, sm: 6, md: "grow" }}>
            <RpImageTextInline
              textProps={{ style: { display: "grid" } }}
              alignCenter={false}
              svgIcon="month_calender"
              imageProps={{ color: "primary" }}
              text={
                <>
                  <Typography variant="caption">Mailing Date</Typography>
                  <Typography
                    className="title-val fw-b mt-2"
                    component="p"
                    variant="caption"
                  >
                    {mailingDate}
                  </Typography>
                </>
              }
            />
          </Grid>
        )}

        {hasCouponExpiration && (
          <Grid
            item
            size={{
              xs: couponExpirationBack ? 6 : 12,
              sm: 6,
              md: "grow",
            }}
          >
            {couponExpirationFront && (
              <RpImageTextInline
                textProps={{ style: { display: "grid" } }}
                alignCenter={false}
                svgIcon="month_calender"
                imageProps={{ color: "primary" }}
                text={
                  <>
                    <Typography variant="caption">
                      Coupon Expiration Date
                    </Typography>
                    <Typography
                      className="title-val fw-b mt-2"
                      component="p"
                      variant="caption"
                    >
                      {couponExpirationFront}
                      {isPrintTypeValue && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.primary"
                          sx={{ ml: 0.5, fontWeight: 600 }}
                        >
                          (Front)
                        </Typography>
                      )}
                    </Typography>
                  </>
                }
              />
            )}

            {isPrintTypeValue && couponExpirationBack && (
              <div className="mt-4">
                <RpImageTextInline
                  textProps={{ style: { display: "grid" } }}
                  alignCenter={false}
                  svgIcon="month_calender"
                  imageProps={{ color: "primary" }}
                  text={
                    <>
                      <Typography variant="caption">
                        Coupon Expiration Date
                      </Typography>
                      <Typography
                        className="title-val fw-b mt-3"
                        component="p"
                        variant="caption"
                      >
                        {couponExpirationBack}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.primary"
                          sx={{ ml: 0.5, fontWeight: 600 }}
                        >
                          (Back)
                        </Typography>
                      </Typography>
                    </>
                  }
                />
              </div>
            )}
          </Grid>
        )}

        <Grid item size={{ xs: 6, sm: 6, md: "grow" }}>
          <RpImageTextInline
            textProps={{ style: { display: "grid" } }}
            alignCenter={false}
            svgIcon="camera"
            imageProps={{ color: "primary" }}
            text={
              <>
                <Typography variant="caption">
                  <strong className="fw-b">Distribution:</strong>{" "}
                  {distributionValue}
                </Typography>
                <Box className="mt-2">
                  <Typography
                    className="title-val mt-1"
                    component="p"
                    variant="caption"
                  >
                    <span className="fw-b">Quantity:</span>{" "}
                    {distribution?.quantity ? distribution?.quantity : 0}
                  </Typography>

                  <Typography
                    className="title-val mt-2 mt-md-3"
                    component="p"
                    variant="caption"
                  >
                    <span className="fw-b">Cost:</span>{" "}
                    {distribution?.cost || formattedAmount(0)} USD
                  </Typography>
                </Box>
              </>
            }
          />
        </Grid>

        {/* Store Copy Column */}

        <Grid item size={{ xs: 6, sm: 6, md: "grow" }}>
          <RpImageTextInline
            textProps={{ style: { display: "grid" } }}
            alignCenter={false}
            svgIcon="cottage"
            imageProps={{ color: "primary" }}
            text={
              <>
                <Typography variant="caption">
                  <strong className="fw-b">Store Copy:</strong> {storeCopyValue}
                </Typography>
                <Box className="mt-2">
                  <Typography
                    className="title-val mt-1"
                    component="p"
                    variant="caption"
                  >
                    <span className="fw-b">Quantity:</span>{" "}
                    {storeCopy?.quantity ? storeCopy?.quantity : 0}
                  </Typography>

                  <Typography
                    className="title-val mt-2 mt-md-3"
                    component="p"
                    variant="caption"
                  >
                    <span className="fw-b">Cost:</span>{" "}
                    {storeCopy?.cost || formattedAmount(0)} USD
                  </Typography>
                  {locationAddress && (
                    <FormHelperText
                      className="mt-2 fw-sb"
                      sx={{ color: "var(--neutral-700)" }}
                    >
                      <strong>Address:</strong> {locationAddress}
                    </FormHelperText>
                  )}
                </Box>
              </>
            }
          />
        </Grid>

        {/* View Template Column */}
        {templateImage && (
          <Grid item size={{ xs: 6, sm: 6, md: "grow" }}>
            <ViewRRDLocationTemplate
              templateImage={templateImage}
              heroImages={location.heroImages}
              couponImages={location.coupons}
              fixedImages={location.fixedImages}
              printType={location?.printType}
              layoutKey={location?.layout_key}
              frontCouponExpiryDate={location.frontCouponExpiryDate}
              backCouponExpiryDate={location.backCouponExpiryDate}
            >
              <RpImageTextInline
                textProps={{ style: { display: "grid" } }}
                alignCenter={false}
                svgIcon="template"
                imageProps={{ color: "primary" }}
                text={
                  <>
                    <Typography
                      variant="caption"
                      className="text-primary cursor-pointer"
                      sx={{ textDecoration: "underline" }}
                    >
                      View Template
                    </Typography>
                  </>
                }
              />
            </ViewRRDLocationTemplate>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default RRDJoinConfirmLocationCard;
